import { IdentityService } from "company-dashboard/services/identity";
import { Router } from "aurelia-router";
import { autoinject } from "aurelia-framework";
import { IValidation, ToastService } from "shared/framework";
import { Modal } from "shared/framework/services/modal";
import { Operation } from "shared/utilities";

@autoinject
export class ResetPassword
{
	/**
	 * Creates a new instance of the type.
	 * @param modal The `Modal` instance representing the modal.
	 * @param identityService The `IdentityService` instance.
	 * @param toastService The `ToastService` instance.
	 */
	public constructor(modal: Modal, identityService: IdentityService, toastService: ToastService, router: Router)
	{
		this._modal = modal;
		this._identityService = identityService;
		this._toastService = toastService;
		this._router = router;
	}

	private readonly _modal: Modal;
	private readonly _identityService: IdentityService;
	private readonly _toastService: ToastService;
	private readonly _router: Router;
	private passwordReset = false;

	/**
	 * The validation for the modal.
	 */
	protected validation: IValidation;

	/**
	* The new password of the user
	*/
	protected password: string;

	/**
	* The confirmed password
	*/
	protected confirmPassword: string;

	/**
	* The most recent update operation
	*/
	protected updateOperation: Operation;

	/**
	 * The model for the modal.
	 */
	protected model: { token: string, id: string };

	/**
	 * Called by the framework when the modal is activated.
	 * @param model The model to use for the modal.
	 */
	public activate(model: { token: string, id: string }): void
	{
		this.model = model;
	}

	/**
	 * Called by the framework when the modal is deactivated.
	 * @returns True if the password has been reset.
	 */
	public deactivate(): boolean
	{
		return this.passwordReset;
	}

	/**
	 * Check if the password is 8 characters long
	 * contains atleast a number, and letters
	 */
	protected get passwordValid(): boolean
	{
		if (this.password == null)
		{
			return false;
		}

		if (this.password.length < 8)
		{
			return false;
		}

		if (!/\d/.test(this.password))
		{
			return false;
		}

		if (!isNaN(Number(this.password)))
		{
			return false;
		}

		return true;
	}

	/**
	 * Called when the cancel button is clicked.
	 */
	protected async onCloseClick(): Promise<void>
	{
		await this._modal.close();
	}

	/**
	 * Updates the page by fetching the latest data.
	 */
	protected async onResetPassword(): Promise<void>
	{
		this.validation.active = true;

		if (!await this.validation.validate())
		{
			return;
		}

		if (this.updateOperation != null)
		{
			this.updateOperation.abort();
		}

		// Create and execute the operation
		this.updateOperation = new Operation(async signal =>
		{
			try
			{
				await this._identityService.resetPassword(this.password, this.confirmPassword, this.model.token, this.model.id , signal);

				this.passwordReset = true;

				await this._identityService.initial();

				await this._modal.close();
				this._router.navigate("medarbejdere", { replace: true });
			}
			catch (error)
			{
				this._toastService.open(
					"error",
					{
						"error": error
					}
				)
			}
		})
	}
}
